import { PagingParameters } from "@/api/dtos";
import { readTablePreferences } from "@/hooks/useTablePreferences";
import {
  useDeleteComplexLink,
  useGetComplexRelationFields,
  useGetNotRelatedResource,
} from "@/api/related-fields";
import { exemplairesColumnsVisibility } from "@/config/dashboard/exemplaires";
import { exhibitionColumnsVisibility } from "@/config/dashboard/exhibition";
import { FormLayoutElement } from "@/config/forms/general";
import { SupportedLocale } from "@/i18n";
import { TypeDefaultImage } from "@/lib/image";
import {
  Exhibition,
  MultipleSampleInExhibition,
  Multiplesample,
} from "@/lib/types";
import { ColumnDef, Row, VisibilityState } from "@tanstack/react-table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import { LinkDrawerRowActions } from "@/components/table/LinkDrawerRowActions";
import { createActionsColumnDef } from "@/components/table/table-actions-column";
import { LinkableType } from "../LinksManagement";
import { HtmlCell, ImageCell, TextCell } from "../table/CustomCell";
import { DataTable } from "../ui/datatable";
import { Input } from "../ui/input";
import { LinksTableProps } from "./ArtworkInAuction";
import MultiplesampleInExhibitionDrawer from "./drawer/MultiplesampleInExhibitionDrawer";
import { formatMultiplesampleAuthorDisplay } from "./multiplesampleAuthorDisplay";
import {
  ExhibitionLinkGridCard,
  MultiplesampleLinkGridCard,
} from "./relatedLinkGridCards";
import { GridCardWithActions } from "./GridCardWithActions";
import { RelatedLinksListGridLayout } from "./RelatedLinksListGridLayout";

interface ComplexLinkProps {
  id: string;
  multiplesample: Multiplesample | undefined;
  exhibition: Exhibition | undefined;
  type: LinkableType;
  fields: FormLayoutElement[] | undefined;
  /** When viewing from the exemplaire sheet (multiplesample), hide redundant exemplaire columns in the exhibitions table. */
  hideExemplaireContextColumns?: boolean;
}

type MultiplesampleToExhibitionProps =
  LinksTableProps<MultipleSampleInExhibition> & {
    multiplesample: Multiplesample | undefined;
  };

type ExhibitionToMultiplesampleProps =
  LinksTableProps<MultipleSampleInExhibition> & {
    exhibition: Exhibition | undefined;
    type: LinkableType;
  };

const MultiplesampleInExhibitionLinks = ({
  id,
  multiplesample,
  exhibition,
  type,
  fields,
  hideExemplaireContextColumns = false,
}: ComplexLinkProps) => {
  const { t, i18n } = useTranslation();
  const vt = useVisibleT();
  const { data: complexRelation } = useGetComplexRelationFields(
    id,
    "multipleSampleInExhibitions",
    type,
  );
  const { mutate: deleteLink } = useDeleteComplexLink(
    id,
    "multipleSampleInExhibitions",
    type,
  );

  const [searchContent, setSearchContent] = useState<string>("");
  const [isAddMode, setIsAddMode] = useState(false);

  const handleDeleteRelation = useCallback(
    (linkId: string) => {
      deleteLink({
        id: linkId,
        relation: "multipleSampleInExhibition",
      });
    },
    [deleteLink],
  );

  const relatedData = useMemo(
    () => (Array.isArray(complexRelation) ? complexRelation : []),
    [complexRelation],
  );

  const renderMultipleSampleInExhibitionRowActions = useCallback(
    (data: MultipleSampleInExhibition) => {
          const preview =
            type === "exhibition"
              ? {
                  previewType: "multiplesample" as const,
                  previewId: data.multiplesample.id,
                  previewElements: [
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumero,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionLocalisation,
                      type: "text" as const,
                    },
                    {
                      label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                      value:
                        data.multiplesample
                          .exemplaireDansEstampesMultiplesDescriptionNotes,
                      type: "richtext" as const,
                    },
                    {
                      label: t("attachments"),
                      value: data.multiplesample.attachments ?? [],
                      type: "image" as const,
                    },
                  ],
                }
              : type === "multiplesample"
                ? {
                    previewType: "exhibition" as const,
                    previewId: data.exhibition.id,
                    previewElements: [
                      {
                        label: t("expositionsDescriptionNumeroExpo"),
                        value: data.exhibition.expositionsDescriptionNumeroExpo,
                        type: "text" as const,
                      },
                      {
                        label: t("expositionsDescriptionResume"),
                        value: data.exhibition.expositionsDescriptionResume,
                        type: "richtext" as const,
                      },
                    ],
                  }
                : null;

          if (!preview) return null;

          return (
            <LinkDrawerRowActions
              previewType={preview.previewType}
              previewId={preview.previewId}
              previewElements={preview.previewElements}
              onDelete={() => handleDeleteRelation(data.id)}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInExhibitionDrawer
                  fields={fields}
                  data={data}
                  queryId={id}
                  type={type}
                  open={open}
                  onOpenChange={onOpenChange}
                />
              )}
            />
          );
    },
    [t, type, fields, handleDeleteRelation, id],
  );

  const columns: ColumnDef<MultipleSampleInExhibition, any>[] = useMemo(
    () => [
      ...(type === "multiplesample"
        ? [
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.exhibition?.attachments ?? []}
                  domain="exhibition"
                />
              ),
            },
            ...(!hideExemplaireContextColumns
              ? [
                  {
                    accessorKey:
                      "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
                    header: vt(
                      "bo.links.exemplaireRecordNumber",
                      "N° d’enregistrement de l’exemplaire",
                    ),
                    cell: ({
                      row,
                    }: {
                      row: Row<MultipleSampleInExhibition>;
                    }) => (
                      <TextCell
                        value={
                          row.original.multiplesample
                            ?.exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
                        }
                      />
                    ),
                  },
                  {
                    accessorKey: "resumeExemplaire",
                    header: vt("bo.links.exemplaireSummary", "résumé exemplaire"),
                    cell: ({
                      row,
                    }: {
                      row: Row<MultipleSampleInExhibition>;
                    }) => (
                      <HtmlCell
                        value={
                          row.original.multiplesample
                            ?.exemplaireDansEstampesMultiplesDescriptionNotes ??
                          ""
                        }
                      />
                    ),
                  },
                ]
              : []),
            {
              accessorKey: "expositionsDescriptionAnnee",
              header: t("tab_expositionsDescriptionAnnee"),
              size: 10,
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={row.original.exhibition.expositionsDescriptionAnnee}
                />
              ),
            },
            {
              accessorKey: "expositionsDescriptionTitre",
              header: t("tab_expositionsDescriptionTitre"),
              size: 300,
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <HtmlCell
                  value={row.original.exhibition.expositionsDescriptionTitre}
                />
              ),
            },
            {
              accessorKey: "expositionsDescriptionDates",
              header: t("tab_expositionsDescriptionDates"),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={row.original.exhibition.expositionsDescriptionDates}
                />
              ),
            },
            {
              accessorKey: "expositionsDescriptionLieu",
              header: t("tab_expositionsDescriptionLieu"),
              size: 300,
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <div className="flex flex-col items-start text-wrap">
                  <p className="text-[14px] font-[500]">
                    {row.original.exhibition.expositionsDescriptionVille}
                  </p>
                  <p className="text-[14px] font-[400] text-gray-500">
                    {row.original.exhibition.expositionsDescriptionLieu}
                  </p>
                </div>
              ),
            },
          ]
        : [
            {
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
              ),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={
                    row.original.multiplesample
                      .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
                  }
                />
              ),
            },
            {
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
              header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={
                    row.original.multiplesample
                      .exemplaireDansEstampesMultiplesDescriptionNumero
                  }
                />
              ),
            },
            {
              accessorKey: "vignette",
              header: "",
              size: 100,
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <ImageCell
                  value=""
                  attachments={row.original.multiplesample?.attachments ?? []}
                  domain="multiplesample"
                />
              ),
            },
            {
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
              ),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={
                    row.original.multiplesample
                      .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
                  }
                />
              ),
            },
            {
              accessorKey: "author",
              header: t("tab_relatedAuthor"),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={formatMultiplesampleAuthorDisplay(
                    row.original.multiplesample,
                    i18n.language as SupportedLocale,
                  )}
                />
              ),
            },
            {
              accessorKey:
                "exemplaireDansEstampesMultiplesDescriptionLocalisation",
              header: t(
                "exemplaireDansEstampesMultiplesDescriptionLocalisation",
              ),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <TextCell
                  value={
                    row.original.multiplesample
                      .exemplaireDansEstampesMultiplesDescriptionLocalisation
                  }
                />
              ),
            },
            {
              accessorKey: "exemplaireDansEstampesMultiplesDescriptionNotes",
              header: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
              cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
                <HtmlCell
                  value={
                    row.original.multiplesample
                      .exemplaireDansEstampesMultiplesDescriptionNotes ?? ""
                  }
                />
              ),
            },
          ]),
      {
        accessorKey: "multiplesampleDansExpositionsDescriptionNumeroCatalogue",
        header: t("multiplesampleDansExpositionsDescriptionNumeroCatalogue"),
        cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
          <TextCell
            value={
              row.original
                .multiplesampleDansExpositionsDescriptionNumeroCatalogue
            }
          />
        ),
      },
      {
        accessorKey: "multiplesampleDansExpositionsNotes",
        header: t("multiplesampleDansExpositionsNotes"),
        cell: ({ row }: { row: Row<MultipleSampleInExhibition> }) => (
          <HtmlCell
            value={row.original.multiplesampleDansExpositionsNotes ?? ""}
          />
        ),
      },
      createActionsColumnDef<MultipleSampleInExhibition>(({ row }) =>
        renderMultipleSampleInExhibitionRowActions(row.original),
      ),
    ],
    [
      fields,
      handleDeleteRelation,
      hideExemplaireContextColumns,
      id,
      i18n.language,
      t,
      type,
    ],
  );

  const datatableConf = useMemo(() => {
    if (type === "exhibition") {
      return (
        <ExhibitionToMultiplesample
          id={id}
          type={type}
          relatedData={relatedData}
          fields={fields}
          exhibition={exhibition}
          isAddMode={isAddMode}
          columnsLink={columns}
          renderRowActions={renderMultipleSampleInExhibitionRowActions}
          searchContent={searchContent}
          searchInput={
            <div className="flex items-center gap-2">
              <Input
                placeholder={t("search")}
                className="w-80"
                value={searchContent}
                onChange={(e) => setSearchContent(e.target.value)}
              />
            </div>
          }
          listGridTitle={t("multiplesamplesRelated")}
          layoutBackLabel={t("relatedRecords")}
          setIsAddMode={setIsAddMode}
        />
      );
    }
    return (
      <MultiplesampleToExhibition
        id={id}
        relatedData={relatedData}
        fields={fields}
        multiplesample={multiplesample}
        isAddMode={isAddMode}
        columnsLink={columns}
        renderRowActions={renderMultipleSampleInExhibitionRowActions}
        searchContent={searchContent}
        searchInput={
          <div className="flex items-center gap-2">
            <Input
              placeholder={t("search")}
              className="w-80"
              value={searchContent}
              onChange={(e) => setSearchContent(e.target.value)}
            />
          </div>
        }
        listGridTitle={t("exhibitionsRelated")}
        layoutBackLabel={t("relatedRecords")}
        setIsAddMode={setIsAddMode}
      />
    );
  }, [
    type,
    id,
    relatedData,
    fields,
    exhibition,
    isAddMode,
    columns,
    searchContent,
    multiplesample,
    t,
  ]);

  return <div className="w-full">{datatableConf}</div>;
};

const MultiplesampleToExhibition = ({
  id,
  relatedData,
  fields,
  multiplesample,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: MultiplesampleToExhibitionProps) => {
  const { t } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD = "multiplesample-links-exhibition-add";
  const STORAGE_KEY_LINKED = "multiplesample-links-exhibition-linked";
  const defaultVisibility = { ...exhibitionColumnsVisibility, actions: true };
  const [columnVisibilityAdd, setColumnVisibilityAdd] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnVisibilityLinked, setColumnVisibilityLinked] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibility, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibility };
    });
  const [columnsOrderAdd, setColumnsOrderAdd] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinked, setColumnsOrderLinked] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_LINKED);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });

  const { data: notAlreadyRelatedExhibitions } =
    useGetNotRelatedResource<Exhibition>({
      resultType: "exhibition",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
    });

  const columnsExhibitions: ColumnDef<Exhibition, any>[] = useMemo(
    () => [
      {
        accessorKey: "expositionsDescriptionNumeroExpo",
        header: t("tab_expositionsDescriptionNumeroExpo"),
        enableResizing: true,
        cell: ({ row }: { row: Row<Exhibition> }) => (
          <TextCell value={row.original.expositionsDescriptionNumeroExpo} />
        ),
      },
      {
        accessorKey: "expositionsDescriptionVignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Exhibition> }) =>
          row.original.attachments ? (
            <ImageCell
              value="expositionsDescriptionVignette"
              attachments={row.original.attachments}
              domain="exhibition"
            />
          ) : null,
      },
      {
        accessorKey: "expositionsDescriptionAnnee",
        header: t("tab_expositionsDescriptionAnnee"),
        size: 10,
        cell: ({ row }: { row: Row<Exhibition> }) => (
          <TextCell value={row.original.expositionsDescriptionAnnee} />
        ),
      },
      {
        accessorKey: "expositionsDescriptionTitre",
        header: t("tab_expositionsDescriptionTitre"),
        size: 300,
        cell: ({ row }: { row: Row<Exhibition> }) => (
          <HtmlCell value={row.original.expositionsDescriptionTitre} />
        ),
      },
      {
        accessorKey: "expositionsDescriptionDates",
        header: t("tab_expositionsDescriptionDates"),
        cell: ({ row }: { row: Row<Exhibition> }) => (
          <TextCell value={row.original.expositionsDescriptionDates} />
        ),
      },
      {
        accessorKey: "expositionsDescriptionLieu",
        header: t("tab_expositionsDescriptionLieu"),
        size: 300,
        cell: ({ row }: { row: Row<Exhibition> }) => (
          <div className="flex flex-col items-start text-wrap">
            <p className="text-[14px] font-[500]">
              {row.original.expositionsDescriptionVille}
            </p>
            <p className="text-[14px] font-[400] text-gray-500">
              {row.original.expositionsDescriptionLieu}
            </p>
          </div>
        ),
      },
      createActionsColumnDef<Exhibition>(({ row }: { row: Row<Exhibition> }) => {
          if (!multiplesample) return null;
          return (
            <LinkDrawerRowActions
              previewType="exhibition"
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("expositionsDescriptionNumeroExpo"),
                  value: row.original.expositionsDescriptionNumeroExpo,
                  type: "text",
                },
                {
                  label: t("expositionsDescriptionResume"),
                  value: row.original.expositionsDescriptionResume,
                  type: "richtext",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="multiplesample"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    multiplesample,
                    exhibition: row.original,
                    multiplesampleDansExpositionsDescriptionNumeroCatalogue: "",
                    multiplesampleDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [id, multiplesample, fields, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsExhibitions}
      data={notAlreadyRelatedExhibitions?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedExhibitions?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAdd,
        onColumnOrderChange: setColumnsOrderAdd,
        state: {
          columnVisibility: columnVisibilityAdd,
          columnOrder: columnsOrderAdd,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinked,
        onColumnOrderChange: setColumnsOrderLinked,
        state: {
          columnVisibility: columnVisibilityLinked,
          columnOrder: columnsOrderLinked,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <ExhibitionLinkGridCard
                  item={row.exhibition}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultiplesampleInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="multiplesample"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

const ExhibitionToMultiplesample = ({
  id,
  type,
  relatedData,
  fields,
  exhibition,
  isAddMode,
  columnsLink,
  renderRowActions,
  searchContent,
  searchInput,
  listGridTitle,
  layoutBackLabel,
  setIsAddMode,
}: ExhibitionToMultiplesampleProps) => {
  const { t, i18n } = useTranslation();
  const [selectedRelationId, setSelectedRelationId] = useState<string | null>(
    null,
  );
  const [getPagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [relatedPagination, setRelatedPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    setRelatedPagination((prev) => ({ ...prev, page: 0 }));
  }, [isAddMode]);

  const STORAGE_KEY_ADD_MS = "exhibition-links-multiplesample-add";
  const STORAGE_KEY_LINKED_MS = "exhibition-links-multiplesample-linked";
  const defaultVisibilityMS = {
    ...exemplairesColumnsVisibility,
    author: true,
    actions: true,
  };
  const [columnVisibilityAddMS, setColumnVisibilityAddMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnVisibilityLinkedMS, setColumnVisibilityLinkedMS] =
    useState<VisibilityState>(() => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { visibility } = saved;
          if (visibility && typeof visibility === "object")
            return { ...defaultVisibilityMS, ...visibility };
        }
      } catch {
        // ignore
      }
      return { ...defaultVisibilityMS };
    });
  const [columnsOrderAddMS, setColumnsOrderAddMS] = useState<string[]>(() => {
    try {
      const saved = readTablePreferences(STORAGE_KEY_ADD_MS);
      if (saved) {
        const { order, columnOrder } = saved;
        const storedOrder = Array.isArray(order)
          ? order
          : Array.isArray(columnOrder)
            ? columnOrder
            : null;
        if (Array.isArray(storedOrder)) {
          const normalizedOrder = [...new Set(storedOrder)] as string[];
          return normalizedOrder.includes("actions")
            ? [...normalizedOrder.filter((id) => id !== "actions"), "actions"]
            : normalizedOrder;
        }
      }
    } catch {
      // ignore
    }
    return [];
  });
  const [columnsOrderLinkedMS, setColumnsOrderLinkedMS] = useState<string[]>(
    () => {
      try {
        const saved = readTablePreferences(STORAGE_KEY_LINKED_MS);
        if (saved) {
          const { order, columnOrder } = saved;
          const storedOrder = Array.isArray(order)
            ? order
            : Array.isArray(columnOrder)
              ? columnOrder
              : null;
          if (Array.isArray(storedOrder)) {
            const normalizedOrder = [...new Set(storedOrder)] as string[];
            return normalizedOrder.includes("actions")
              ? [
                  ...normalizedOrder.filter((id) => id !== "actions"),
                  "actions",
                ]
              : normalizedOrder;
          }
        }
      } catch {
        // ignore
      }
      return [];
    },
  );

  const { data: notAlreadyRelatedMultiplesamples } =
    useGetNotRelatedResource<Multiplesample>({
      resultType: "multiplesample",
      resourceId: id,
      pagination: getPagination,
      searchQuery: searchContent,
      anchorResourceType: type,
    });

  const columnsMultiplesamples: ColumnDef<Multiplesample, any>[] = useMemo(
    () => [
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        header: t(
          "exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement",
        ),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroEnregistrement
            }
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionNumero",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original.exemplaireDansEstampesMultiplesDescriptionNumero
            }
          />
        ),
      },
      {
        accessorKey: "vignette",
        header: "",
        size: 100,
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <ImageCell
            value=""
            attachments={row.original.attachments ?? []}
            domain="multiplesample"
          />
        ),
      },
      {
        accessorKey:
          "exemplaireDansEstampesMultiplesDescriptionNumeroInventaire",
        header: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire
            }
          />
        ),
      },
      {
        accessorKey: "author",
        header: t("tab_relatedAuthor"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={formatMultiplesampleAuthorDisplay(
              row.original,
              i18n.language as SupportedLocale,
            )}
          />
        ),
      },
      {
        accessorKey: "exemplaireDansEstampesMultiplesDescriptionLocalisation",
        header: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
        cell: ({ row }: { row: Row<Multiplesample> }) => (
          <TextCell
            value={
              row.original
                .exemplaireDansEstampesMultiplesDescriptionLocalisation
            }
          />
        ),
      },
      createActionsColumnDef<Multiplesample>(({ row }: { row: Row<Multiplesample> }) => {
          if (!exhibition) return null;
          return (
            <LinkDrawerRowActions
              previewType={"multiplesample" as TypeDefaultImage}
              previewId={row.original.id}
              previewElements={[
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumero"),
                  value:
                    row.original.exemplaireDansEstampesMultiplesDescriptionNumero,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNumeroInventaire"),
                  value:
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionNumeroInventaire,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionLocalisation"),
                  value:
                    row.original
                      .exemplaireDansEstampesMultiplesDescriptionLocalisation,
                  type: "text",
                },
                {
                  label: t("exemplaireDansEstampesMultiplesDescriptionNotes"),
                  value:
                    row.original.exemplaireDansEstampesMultiplesDescriptionNotes,
                  type: "richtext",
                },
                {
                  label: t("attachments"),
                  value: row.original.attachments ?? [],
                  type: "image",
                },
              ]}
              editLabel={t("actions.add")}
              editDrawer={({ open, onOpenChange }) => (
                <MultiplesampleInExhibitionDrawer
                  key={row.original.id}
                  fields={fields}
                  queryId={id}
                  type="exhibition"
                  open={open}
                  onOpenChange={onOpenChange}
                  data={{
                    id: "",
                    multiplesample: row.original,
                    exhibition,
                    multiplesampleDansExpositionsDescriptionNumeroCatalogue: "",
                    multiplesampleDansExpositionsNotes: "",
                  }}
                />
              )}
            />
          );
        }),
    ],
    [id, exhibition, fields, i18n.language, t],
  );

  const paginatedRelatedData = useMemo(() => {
    const start = relatedPagination.page * relatedPagination.pageSize;
    const end = start + relatedPagination.pageSize;
    return (relatedData ?? []).slice(start, end);
  }, [relatedData, relatedPagination.page, relatedPagination.pageSize]);

  const relatedPaginationProps = {
    pagination: relatedPagination,
    setPage: setRelatedPagination,
    totalSize: (relatedData ?? []).length,
  };
  const addModeTable = (
    <DataTable
      columns={columnsMultiplesamples}
      data={notAlreadyRelatedMultiplesamples?.data ?? []}
      pagination={{
        pagination: getPagination,
        setPage: setPagination,
        totalSize: notAlreadyRelatedMultiplesamples?.totalSize ?? 0,
      }}
      storageKey={STORAGE_KEY_ADD_MS}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityAddMS,
        onColumnOrderChange: setColumnsOrderAddMS,
        state: {
          columnVisibility: columnVisibilityAddMS,
          columnOrder: columnsOrderAddMS,
        },
      }}
    />
  );
  const listTable = (
    <DataTable
      columns={columnsLink}
      data={paginatedRelatedData}
      pagination={relatedPaginationProps}
      storageKey={STORAGE_KEY_LINKED_MS}
      options={{
        enableColumnResizing: true,
        onColumnVisibilityChange: setColumnVisibilityLinkedMS,
        onColumnOrderChange: setColumnsOrderLinkedMS,
        state: {
          columnVisibility: columnVisibilityLinkedMS,
          columnOrder: columnsOrderLinkedMS,
        },
      }}
    />
  );
  if (listGridTitle && setIsAddMode && searchInput) {
    return (
      <RelatedLinksListGridLayout
        storageKey={STORAGE_KEY_LINKED_MS}
        title={listGridTitle}
        backLabel={layoutBackLabel}
        isAddMode={isAddMode}
        onBack={() => setIsAddMode(false)}
        onToggleAdd={() => setIsAddMode(true)}
        searchInput={searchInput}
        addModeTable={addModeTable}
        listTable={listTable}
        gridContent={
          <>
            {paginatedRelatedData.map((row) => (
              <div key={row.id}>
                <GridCardWithActions actions={renderRowActions?.(row)}>
                  <MultiplesampleLinkGridCard
                  item={row.multiplesample}
                  t={t}
                  onTextClick={() => setSelectedRelationId(row.id)}
                />
                </GridCardWithActions>
                                <MultiplesampleInExhibitionDrawer
                  fields={fields}
                  data={row}
                  queryId={id}
                  type="exhibition"
                  open={selectedRelationId === row.id}
                  onOpenChange={(open) => {
                    if (!open) setSelectedRelationId(null);
                  }}
                />
              </div>
            ))}
          </>
        }
        relatedPagination={relatedPaginationProps}
      />
    );
  }
  return isAddMode ? addModeTable : listTable;
};

export default MultiplesampleInExhibitionLinks;
